import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { ref as dbRef, get, push, remove } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import {
  BookOpen, ShoppingBag, Package, X, Camera, AlertCircle,
  ChevronDown, MessageCircle, Loader, Plus, ArrowLeft
} from "lucide-react";

const CATEGORIES = [
  "All",
  "School Books",
  "School Uniform",
  "School Bag",
  "Stationery",
  "Sports Equipment",
  "Musical Instruments",
  "Science Kit",
  "School Shoes",
  "Lunch Box & Bottle",
  "Art & Craft",
  "Other School Items",
];

const POST_CATEGORIES = CATEGORIES.filter(c => c !== "All");

const CLASS_FILTERS = [
  { label: "All Classes", value: "all" },
  { label: "Primary (1-5)", value: "primary" },
  { label: "Middle (6-8)", value: "middle" },
  { label: "Secondary (9-10)", value: "secondary" },
  { label: "Senior (11-12)", value: "senior" },
];

const CHILD_CLASSES = [
  "Nursery", "LKG", "UKG",
  "Class 1", "Class 2", "Class 3", "Class 4", "Class 5",
  "Class 6", "Class 7", "Class 8",
  "Class 9", "Class 10",
  "Class 11", "Class 12",
  "Graduation / College",
];

const CONDITIONS = ["New", "Like New", "Good", "Used"];

function classMatchesFilter(childClass, filter) {
  if (filter === "all") return true;
  const primaryClasses = ["Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Nursery", "LKG", "UKG"];
  const middleClasses = ["Class 6", "Class 7", "Class 8"];
  const secondaryClasses = ["Class 9", "Class 10"];
  const seniorClasses = ["Class 11", "Class 12"];
  if (filter === "primary") return primaryClasses.includes(childClass);
  if (filter === "middle") return middleClasses.includes(childClass);
  if (filter === "secondary") return secondaryClasses.includes(childClass);
  if (filter === "senior") return seniorClasses.includes(childClass);
  return true;
}

function ListingCard({ listing }) {
  const waLink = `https://wa.me/91${listing.whatsapp?.replace(/\D/g, "")}?text=${encodeURIComponent(
    `Hi! I saw your listing on CampusSwap Parent Zone. You posted "${listing.title}" for ${listing.price === 0 ? "Free" : "₹" + listing.price}. Is it still available?`
  )}`;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="h-32 bg-gray-100 overflow-hidden relative">
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen size={36} className="text-gray-200" />
          </div>
        )}
        {listing.price === 0 && (
          <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">FREE</span>
        )}
      </div>
      <div className="p-3">
        <p className="font-semibold text-gray-800 text-sm line-clamp-1">{listing.title}</p>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full uppercase">{listing.childClass}</span>
        </div>
        <p className="text-gray-400 text-xs mt-1 line-clamp-1">{listing.schoolName}</p>
        <p className="text-blue-600 font-bold text-sm mt-1">
          {listing.price === 0 ? "Free" : `₹${listing.price}`}
        </p>
        {listing.whatsapp && (
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="mt-2 w-full flex items-center justify-center gap-1.5 bg-green-500 text-white text-xs py-2 rounded-lg font-semibold hover:bg-green-600"
          >
            <MessageCircle size={13} />
            Contact Parent
          </a>
        )}
      </div>
    </div>
  );
}

export default function ParentZone() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("buy");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [classFilter, setClassFilter] = useState("all");
  const [showPostSheet, setShowPostSheet] = useState(false);

  // Post form state
  const [form, setForm] = useState({
    title: "",
    category: "",
    childClass: "",
    schoolName: "",
    condition: "Good",
    price: "",
    description: "",
    whatsapp: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchListings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchListings() {
    setLoading(true);
    try {
      const snap = await get(dbRef(db, "parent_listings"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        const active = all
          .filter(l => l.status === "active")
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setListings(active);
      } else {
        setListings([]);
      }
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  }

  function handleImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setFormError("Image must be under 5MB.");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function openPostSheet() {
    setForm({
      title: "",
      category: "",
      childClass: userProfile?.childClass || "",
      schoolName: userProfile?.childSchool || "",
      condition: "Good",
      price: "",
      description: "",
      whatsapp: userProfile?.phone || "",
    });
    setImageFile(null);
    setImagePreview(null);
    setFormError("");
    setShowPostSheet(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");

    if (!form.title.trim()) return setFormError("Please enter item name.");
    if (!form.category) return setFormError("Please select a category.");
    if (!form.childClass) return setFormError("Please select the class.");
    if (!form.schoolName.trim()) return setFormError("Please enter school name.");
    if (form.price === "" || form.price === null) return setFormError("Please enter a price (0 for free).");
    if (!/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, ""))) {
      return setFormError("Enter a valid 10-digit WhatsApp number.");
    }

    setSubmitting(true);
    try {
      let imageUrl = "";
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      await push(dbRef(db, "parent_listings"), {
        title: form.title.trim(),
        category: form.category,
        childClass: form.childClass,
        schoolName: form.schoolName.trim(),
        condition: form.condition,
        price: Number(form.price),
        description: form.description.trim(),
        whatsapp: form.whatsapp.replace(/\s/g, ""),
        imageUrl,
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || "Parent",
        city: userProfile?.city || "",
        status: "active",
        createdAt: new Date().toISOString(),
      });

      setShowPostSheet(false);
      fetchListings();
    } catch (err) {
      
      setFormError("Failed to post item. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const filteredListings = listings.filter(l => {
    const catMatch = categoryFilter === "All" || l.category === categoryFilter;
    const classMatch = classMatchesFilter(l.childClass, classFilter);
    return catMatch && classMatch;
  });

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Parent Zone</h1>
              <p className="text-amber-100 text-xs mt-0.5">Buy & sell school items for your children</p>
            </div>
          </div>
          <button
            onClick={openPostSheet}
            className="flex items-center gap-1.5 bg-white text-amber-600 px-3 py-2 rounded-xl text-sm font-bold shadow-sm"
          >
            <Plus size={16} />
            Post Item
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-100 mx-4 mt-4 rounded-xl p-1">
        <button
          onClick={() => setTab("buy")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            tab === "buy" ? "bg-white text-amber-600 shadow-sm" : "text-gray-500"
          }`}
        >
          <ShoppingBag size={16} />
          Buy Items
        </button>
        <button
          onClick={() => setTab("sell")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            tab === "sell" ? "bg-white text-amber-600 shadow-sm" : "text-gray-500"
          }`}
        >
          <Package size={16} />
          Sell Items
        </button>
      </div>

      {tab === "buy" && (
        <div className="mt-4">
          {/* Category Filter */}
          <div className="flex gap-2 px-4 overflow-x-auto pb-2 hide-scrollbar">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 border transition-all ${
                  categoryFilter === cat
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Class Filter */}
          <div className="flex gap-2 px-4 overflow-x-auto pb-2 mt-2 hide-scrollbar">
            {CLASS_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setClassFilter(f.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 border transition-all ${
                  classFilter === f.value
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="px-4 mt-3">
            {loading ? (
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                    <div className="h-32 bg-gray-200" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredListings.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl">
                <div className="text-5xl mb-3">📚</div>
                <p className="font-semibold text-gray-700">No items found</p>
                <p className="text-gray-400 text-sm mt-1">Be the first to post a school item!</p>
                <button
                  onClick={openPostSheet}
                  className="mt-4 bg-amber-500 text-white px-5 py-2.5 rounded-xl text-sm font-semibold"
                >
                  Post First Item
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {filteredListings.map(l => <ListingCard key={l.id} listing={l} />)}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "sell" && (
        <div className="px-4 mt-4">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-2xl p-5 text-center">
            <div className="text-4xl mb-3">🎒</div>
            <h3 className="font-bold text-gray-800 text-lg">Sell School Items</h3>
            <p className="text-gray-500 text-sm mt-1 mb-4">
              Post school books, uniforms, bags and other items your children no longer need. Connect directly with other parents!
            </p>
            <button
              onClick={openPostSheet}
              className="bg-amber-500 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-amber-600 flex items-center gap-2 mx-auto"
            >
              <Plus size={18} />
              Post New Item
            </button>
          </div>

          {/* My listings */}
          {currentUser && (
            <div className="mt-4">
              <h3 className="font-bold text-gray-800 mb-3">My Posted Items</h3>
              {listings.filter(l => l.postedBy === currentUser.uid).length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-6">You haven't posted anything yet.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {listings
                    .filter(l => l.postedBy === currentUser.uid)
                    .map(l => (
                      <MyListingCard key={l.id} listing={l} onDelete={fetchListings} />
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Post Item Bottom Sheet */}
      {showPostSheet && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPostSheet(false)} />
          <div className="relative bg-white rounded-t-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-4 flex items-center justify-between rounded-t-3xl z-10">
              <h3 className="font-bold text-gray-800 text-lg">Post School Item</h3>
              <button onClick={() => setShowPostSheet(false)} className="p-2 rounded-full hover:bg-gray-100">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4 pb-24">
              {formError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  {formError}
                </div>
              )}

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Item Photo</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl h-36 flex flex-col items-center justify-center cursor-pointer hover:border-amber-400 transition-colors overflow-hidden"
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <Camera size={28} className="text-gray-300 mb-2" />
                      <p className="text-sm text-gray-400">Tap to add photo</p>
                      <p className="text-xs text-gray-300 mt-0.5">Max 5MB</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </div>

              {/* Item Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Item Name *</label>
                <input
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Class 5 NCERT Math Book"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Category *</label>
                <div className="relative">
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm appearance-none bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent text-gray-700"
                    required
                  >
                    <option value="">Select Category</option>
                    {POST_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Child's Class */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Child's Class *</label>
                <div className="relative">
                  <select
                    value={form.childClass}
                    onChange={e => setForm({ ...form, childClass: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm appearance-none bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent text-gray-700"
                    required
                  >
                    <option value="">Select Class</option>
                    {CHILD_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* School Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">School Name *</label>
                <input
                  value={form.schoolName}
                  onChange={e => setForm({ ...form, schoolName: e.target.value })}
                  placeholder="Child's school name"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  required
                />
              </div>

              {/* Condition */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Condition *</label>
                <div className="flex gap-2 flex-wrap">
                  {CONDITIONS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, condition: c })}
                      className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                        form.condition === c
                          ? "bg-amber-500 text-white border-amber-500"
                          : "bg-white text-gray-600 border-gray-200"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Price (₹) *</label>
                <input
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={e => setForm({ ...form, price: e.target.value })}
                  placeholder="Enter 0 for Free"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Describe the item, edition, any defects..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none"
                />
              </div>

              {/* WhatsApp */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">WhatsApp Number *</label>
                <input
                  type="tel"
                  value={form.whatsapp}
                  onChange={e => setForm({ ...form, whatsapp: e.target.value })}
                  placeholder="10-digit number"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-amber-500 text-white py-3.5 rounded-xl font-bold text-sm hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Posting...
                  </>
                ) : (
                  "Post Item"
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MyListingCard({ listing, onDelete }) {
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!window.confirm("Delete this listing?")) return;
    setDeleting(true);
    try {
      await remove(dbRef(db, `parent_listings/${listing.id}`));
      onDelete();
    } catch (err) {
      
      alert("Failed to delete. Try again.");
      setDeleting(false);
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="h-28 bg-gray-100 overflow-hidden relative">
        {listing.imageUrl ? (
          <img src={listing.imageUrl} alt={listing.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen size={28} className="text-gray-200" />
          </div>
        )}
      </div>
      <div className="p-2.5">
        <p className="font-semibold text-gray-800 text-sm line-clamp-1">{listing.title}</p>
        <p className="text-blue-600 font-bold text-xs mt-0.5">
          {listing.price === 0 ? "Free" : `₹${listing.price}`}
        </p>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="mt-2 w-full text-xs text-red-500 border border-red-200 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-50"
        >
          {deleting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </div>
  );
}
